import { useCallback, useEffect, useState } from "react";
import { SelectV2Value, SelectV2Option } from "../SelectV2.types.js";

type UseKeyboardNavigationProps<Value extends SelectV2Value> = {
    isMenuOpen: boolean;
    options: SelectV2Option<Value>[];
    onOptionSelect: (value: Value) => void;
    openMenu: () => void;
    closeMenu: () => void;
    selectedIndex: number;
    searchable: boolean;
};

export const useKeyboardNavigation = <Value extends SelectV2Value>({
    isMenuOpen,
    options,
    onOptionSelect,
    openMenu,
    closeMenu,
    selectedIndex,
    searchable,
}: UseKeyboardNavigationProps<Value>) => {
    const [focusedIndex, setFocusedIndex] = useState<number>(selectedIndex);

    useEffect(() => {
        setFocusedIndex(selectedIndex);
    }, [selectedIndex]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            switch (e.key) {
                case "Escape":
                    e.preventDefault();
                    if (isMenuOpen) closeMenu();
                    else openMenu();
                    break;
                case "ArrowDown":
                    e.preventDefault();
                    setFocusedIndex((prev) => {
                        const nextIndex = prev + 1;
                        return nextIndex >= options.length ? 0 : nextIndex;
                    });
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setFocusedIndex((prev) => {
                        const nextIndex = prev - 1;
                        return nextIndex < 0 ? options.length - 1 : nextIndex;
                    });
                    break;
                case " ":
                    if (!searchable) {
                        e.preventDefault();
                        if (!isMenuOpen) {
                            openMenu();
                        }
                    }
                    break;

                case "Enter":
                    e.preventDefault();
                    if (!isMenuOpen) openMenu();
                    if (focusedIndex >= 0 && focusedIndex < options.length) {
                        onOptionSelect(options[focusedIndex].value);
                    }
                    break;
                default:
                    break;
            }
        },
        [isMenuOpen, focusedIndex, options, onOptionSelect, openMenu, closeMenu],
    );

    return {
        focusedIndex,
        handleKeyDown,
    };
};
